import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHIFTS = [
  { id: "morning", label: "Morning" },
  { id: "afternoon", label: "Afternoon" },
  { id: "night", label: "Night" },
];

// Auto-detect current shift based on IST hour.
// Morning: 06:00-13:59 | Afternoon: 14:00-21:59 | Night: 22:00-05:59
const detectShiftIST = () => {
  const nowUtcMs = Date.now();
  const istMs = nowUtcMs + 5.5 * 60 * 60 * 1000;
  const istHour = new Date(istMs).getUTCHours();
  if (istHour >= 6 && istHour < 14) return "morning";
  if (istHour >= 14 && istHour < 22) return "afternoon";
  return "night";
};

const normalizeStatus = (status) => {
  if (!status) return "";
  const s = status.toString().toLowerCase().replace(/\s+/g, "");
  if (s === "running") return "Running";
  if (s === "undersizechange" || s === "undersetting" || s === "setting") return "Under Size Change";
  if (s === "breakdown") return "Breakdown";
  if (
    s === "stop" ||
    s === "stopped" ||
    s.includes("stopped") ||
    s.includes("operationalreasons")
  )
    return "Stopped due to Operational Reasons";
  if (s === "statusnotentered" || s === "notentered") return "Status not Entered";
  return status;
};

const getStatusClass = (status) => {
  const n = normalizeStatus(status);
  if (n === "Running") return "status-running";
  if (n === "Under Size Change") return "status-size-change";
  if (n === "Breakdown") return "status-breakdown";
  if (n === "Stopped due to Operational Reasons") return "status-stopped";
  if (n === "Status not Entered") return "status-not-entered";
  return "status-running";
};

const getBadgeClass = (status) => {
  const n = normalizeStatus(status);
  if (n === "Running") return "badge-running";
  if (n === "Under Size Change") return "badge-size-change";
  if (n === "Breakdown") return "badge-breakdown";
  if (n === "Stopped due to Operational Reasons") return "badge-stopped";
  if (n === "Status not Entered") return "badge-not-entered";
  return "badge-running";
};

const getIndicatorColor = (status) => {
  const n = normalizeStatus(status);
  if (n === "Running") return "#34D399";
  if (n === "Under Size Change") return "#FACC15";
  if (n === "Breakdown") return "#FB923C";
  if (n === "Stopped due to Operational Reasons") return "#F87171";
  if (n === "Status not Entered") return "#6B7280";
  return "#34D399";
};

function MachineCard({ machine }) {
  const currentStatus = machine.status;
  const displayStatus = normalizeStatus(currentStatus) || currentStatus;
  const indicatorColor = getIndicatorColor(currentStatus);

  return (
    <div
      className={`flex flex-col justify-between p-1.5 border ${getStatusClass(currentStatus)}`}
      data-testid={`machine-card-${machine.machine_name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div>
        <div className="flex items-center gap-1 mb-0.5">
          <div
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: indicatorColor, boxShadow: `0 0 4px ${indicatorColor}` }}
          ></div>
          <h3
            className="text-[10px] font-mono font-bold tracking-tight leading-tight"
            data-testid={`machine-name-${machine.machine_name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {machine.machine_name}
          </h3>
        </div>
        {machine.time && (
          <div className="text-[8px] font-mono font-semibold leading-tight">{machine.time}</div>
        )}
        {machine.remarks && machine.remarks !== 'None' && (
          <div
            className="text-[7px] font-mono font-bold mt-0.5 bg-black/50 px-1 py-0.5 inline-block leading-tight"
            style={{ color: '#FFA500' }}
          >
            ⚠ {machine.remarks.toUpperCase()}
          </div>
        )}
      </div>

      <div
        className={`mt-1 text-center py-0.5 px-1 font-mono text-[7px] font-bold uppercase tracking-wider ${getBadgeClass(currentStatus)}`}
        data-testid={`status-badge-${machine.machine_name.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {displayStatus}
      </div>
    </div>
  );
}

function App() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeShift, setActiveShift] = useState(detectShiftIST());

  const fetchMachines = async (date, shift) => {
    setLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/machines`, {
        params: { date: dateStr, shift },
      });
      setMachines(response.data);
    } catch (error) {
      console.error("Error fetching machines:", error);
      toast.error("Failed to load machines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines(currentDate, activeShift);
  }, [currentDate, activeShift]);

  const changeDate = (days) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const boltForgingMachines = machines.filter(
    (m) => m.group && m.group.toLowerCase().includes('bolt forging')
  );
  const nutForgingMachines = machines.filter(
    (m) => m.group && m.group.toLowerCase().includes('nut forming')
  );

  const activeShiftLabel = SHIFTS.find((s) => s.id === activeShift)?.label || 'Morning';

  return (
    <div className="w-screen h-screen overflow-hidden flex flex-col px-2 pt-12 pb-2 bg-[#0A0A0A]">
      <Toaster position="top-center" />

      <header className="flex flex-col items-center mb-1 shrink-0">
        <h1
          className="text-base font-mono font-bold tracking-wider text-center text-white leading-tight"
          data-testid="dashboard-title"
        >
          SIMPLEX DAILY MACHINE STATUS
        </h1>
        <p
          className="text-[7px] text-[#A3A3A3] uppercase tracking-[0.3em] font-mono"
          data-testid="dashboard-subtitle"
        >
          {activeShiftLabel} Dashboard
        </p>

        {/* Shift selector */}
        <div
          className="flex items-stretch mt-1 border border-[#404040] overflow-hidden"
          data-testid="shift-selector"
        >
          {SHIFTS.map((s) => {
            const isActive = activeShift === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActiveShift(s.id)}
                data-testid={`shift-tab-${s.id}`}
                className={`px-2 py-0.5 text-[8px] font-mono font-bold uppercase tracking-[0.2em] border-r border-[#404040] last:border-r-0 transition-colors ${
                  isActive
                    ? 'bg-white text-black'
                    : 'bg-[#0A0A0A] text-[#A3A3A3] hover:bg-[#1A1A1A]'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => changeDate(-1)}
            className="p-0.5 hover:bg-[#1A1A1A] border border-[#404040] text-white"
            data-testid="prev-day-button"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>

          <div className="text-[10px] font-mono text-white font-semibold" data-testid="current-date">
            {formatDate(currentDate)}
          </div>

          <button
            onClick={() => changeDate(1)}
            className="p-0.5 hover:bg-[#1A1A1A] border border-[#404040] text-white"
            data-testid="next-day-button"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center text-[#A3A3A3] flex-1" data-testid="loading-indicator">
          Loading machines...
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-1 min-h-0">
          {boltForgingMachines.length > 0 && (
            <div>
              <h2
                className="text-[8px] font-mono font-bold uppercase tracking-[0.3em] text-[#A3A3A3] mb-1 pl-0.5"
                data-testid="bolt-forging-heading"
              >
                Bolt Forging
              </h2>
              <div className="grid grid-cols-2 gap-1" data-testid="bolt-forging-grid">
                {boltForgingMachines.map((machine, index) => (
                  <MachineCard key={index} machine={machine} />
                ))}
              </div>
            </div>
          )}

          {nutForgingMachines.length > 0 && (
            <div>
              <h2
                className="text-[8px] font-mono font-bold uppercase tracking-[0.3em] text-[#A3A3A3] mb-1 pl-0.5"
                data-testid="nut-forging-heading"
              >
                Nut Forging
              </h2>
              <div
                className="grid grid-cols-2 gap-1"
                style={{ gridAutoRows: 'auto' }}
                data-testid="nut-forging-grid"
              >
                {nutForgingMachines.map((machine, index) => (
                  <MachineCard key={index} machine={machine} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
